import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { ValidationErrorsService } from '@avano/avano-components/avano-validation/validation-errors-service';
import { FormControl, Validators } from '@angular/forms';
import { NGXLogger } from 'ngx-logger';

import {
  DossierControllerService,
  DossierDto,
  DossierDtoRequestedTypeEnum,
  DossierDtoTypeEnum,
  OrganizationDto,
  OtherArticleNamesDto,
  ProductInternalResponseDto,
  ProductSearchResultDto
} from '@scip/api';
import { forkJoin, ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ProductUtils, translationKeys } from '../../../core';
import { BackendErrorService, EnvironmentConfigurationService, NotificationService, ProfileService } from '../../../services';
import { AvanoLoadingIndicatorService } from '@avano/avano-components';
import { HttpResponse } from '@angular/common/http';

@Component({
  selector: 'scip-upload-product-dialog',
  templateUrl: './upload-product-dialog.component.html',
  styleUrls: ['./upload-product-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadProductDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() products: ProductSearchResultDto[] | ProductInternalResponseDto[];

  @Output() readonly closeEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  showDossierUploadDialog = true;
  dossierData: DossierDto[] = [];
  blockingIdentifier = 'dossierUploadTable';
  translationKeys: typeof translationKeys = translationKeys;
  displayOtherNameSelection = false;
  otherArticleNames: FormControl = new FormControl([]);
  selectedOrganization: FormControl = new FormControl(undefined, [Validators.required]);
  organizationsSuggestions: OrganizationDto[] = [];
  validationErrors = [];
  forceResubmission = false;
  allowForceResubmission = false;

  //Dossier type selection
  selectedRequestType: FormControl = new FormControl(DossierDtoRequestedTypeEnum.Standard);
  selectedDossierType: DossierDtoRequestedTypeEnum = DossierDtoRequestedTypeEnum.Standard;
  allDossierTypes: { name: string; key: DossierDtoRequestedTypeEnum }[] = [
    { name: translationKeys.dossierDto.requestedType.STANDARD, key: DossierDtoRequestedTypeEnum.Standard },
    {
      name: translationKeys.dossierDto.requestedType.PARTS_ONLY,
      key: DossierDtoRequestedTypeEnum.PartsOnly
    },
    {
      name: translationKeys.dossierDto.requestedType.REFERENCING,
      key: DossierDtoRequestedTypeEnum.Referencing
    }
  ];

  private allOrganizations: OrganizationDto[] = [];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private dossierControllerService: DossierControllerService,
    private profileService: ProfileService,
    private notificationService: NotificationService,
    private backendErrorService: BackendErrorService,
    private changeDet: ChangeDetectorRef,
    private avanoLoadingIndicatorService: AvanoLoadingIndicatorService,
    private logger: NGXLogger,
    @Optional() private validationService: ValidationErrorsService,
    private environmentConfigurationService: EnvironmentConfigurationService,
  ) {}

  ngOnInit(): void {
    // We want our calls to be executed in parallel, but we need the answer of both to continue
    forkJoin([
      this.profileService.getAllOrganizations(true, true, true),
      this.dossierControllerService.getSuggestions(ProductUtils.getIdentifiersForProducts(this.products))
    ]).subscribe(([organizations, dossierSuggestions]: [OrganizationDto[], DossierDto[]]) => {
      this.handleDossierSuggestions(dossierSuggestions);
      this.handleOrganizations(organizations);

      // We know we filled our organizations, products and suggestions, so lets now prefill inputs if possible
      this.preselectOrganization();
      this.preselectRequestType();

      // We know we prepared our organizations input and our dossier suggestions
      this.avanoLoadingIndicatorService.unblock([this.blockingIdentifier]);
      this.changeDet.markForCheck();
    });

    this.validationService?.changed$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (errors) => {
        this.validationErrors = errors;
      }
    });

    const { allowForceResubmission } = this.environmentConfigurationService.environment;
    this.allowForceResubmission = allowForceResubmission;
  }

  ngAfterViewInit(): void {
    this.avanoLoadingIndicatorService.block([this.blockingIdentifier]);
  }

  closeDossierUploadDialog(): void {
    this.closeEvent.emit(false);
  }

  closeOtherNameDialog(): void {
    this.displayOtherNameSelection = false;
  }

  openOtherNameDialog(): void {
    this.displayOtherNameSelection = true;
  }

  saveOtherName(otherNames: OtherArticleNamesDto[]): void {
    this.otherArticleNames.setValue([...otherNames]);
    this.displayOtherNameSelection = false;
  }

  uploadDossiers(): void {
    if (this.otherArticleNames.value.length) {
      this.setOtherNamesForDossierUpload();
    }

    if (this.selectedOrganization.value !== undefined) {
      this.setOrganizationForDossierUpload();
    }

    this.setDossierTypeForDossierUpload();

    const dossierData = this.dossierData.map(dossier => ({
      ...dossier,
      forceResubmission: this.forceResubmission
    }));

    this.dossierControllerService
      .upload(dossierData, 'response')
      .pipe(map((response: HttpResponse<null>) => response.status))
      .subscribe(
        (statusCode: number) => {
          this.handleSuccessfulDossierUpload(statusCode);
        },
        (err) => {
          this.backendErrorService.handleError(err, translationKeys.uploadProductDialogComponent.msg.uploadDossierError);
          this.logger.error('Dossier Upload Error: ', err);
        }
      );
  }

  filterDossierDefaultOrganizations(event): void {
    this.organizationsSuggestions = this.allOrganizations.filter((singleOrg: OrganizationDto) => {
      return singleOrg.name.toLowerCase().startsWith(event.query.toLowerCase());
    });
    this.organizationsSuggestions = this.organizationsSuggestions.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }


  private handleOrganizations(allOrganizations: OrganizationDto[]): void {
    this.allOrganizations = allOrganizations;
    this.organizationsSuggestions = this.allOrganizations;
  }

  private handleDossierSuggestions(dossierSuggestions: DossierDto[]): void {
    this.dossierData = dossierSuggestions;
  }

  private preselectOrganization(): void {
    // One or more products means we should take over the organization if it makes sense
    // For one it is easy, but if multiple products are selected we need to check that all have the same org assigned.
    if (this.products?.length && this.dossierData[0].org) {
      const allSameOrg = this.dossierData.every((singleValue: DossierDto, index: number, array: DossierDto[]) => {
        if (singleValue && singleValue.org) {
          return singleValue.org.identifier === array[0].org.identifier;
        } else {
          return false;
        }
      });
      if (allSameOrg) {
        // We know all products/dossierdata have the same org assigned, so we just use the first one
        this.searchAndSetOrganization(this.dossierData[0].org.identifier);
      }
    }

    // We perhaps filled the selectedOrganization already, only if not preselected then we need to search for the
    // default organization. This cannot be an else if, because even if there is a common organization,
    // it still can be an organization which is not selectable (active) anymore and then we want to select the default one
    if (!this.selectedOrganization.value) {
      // Prefill first default organization
      this.selectedOrganization.setValue(this.allOrganizations.find((singleOrg: OrganizationDto) => singleOrg.defaultOrg));
    }
  }

  private searchAndSetOrganization(orgIdentifier: string): void {
    // We need to search within the allOrganizations, because the organization which was used at the product before
    // could already be inactive/non primary and so not selectable anymore.
    this.selectedOrganization.setValue(this.allOrganizations.find((singleOrg: OrganizationDto) => singleOrg.identifier === orgIdentifier));
  }

  private preselectRequestType(): void {
    // If only a single product is selected, we need to preselect requested type
    if (this.products?.length && this.dossierData[0].requestedType) {
      const allSameRequestType = this.dossierData.every((singleValue: DossierDto, index: number, array: DossierDto[]) => {
        return singleValue.requestedType === array[0].requestedType;
      });
      if (allSameRequestType) {
        this.selectedDossierType = this.dossierData[0].requestedType as DossierDtoRequestedTypeEnum; // manual cast from type to our enum
        this.selectedRequestType.setValue(this.selectedDossierType);
      }
    }
  }

  private setOrganizationForDossierUpload() {
    for (const dossier of this.dossierData) {
      dossier.org = this.selectedOrganization.value;
    }
  }

  private setDossierTypeForDossierUpload() {
    for (const dossier of this.dossierData) {
      dossier.requestedType = this.selectedRequestType.value;
      dossier.type = DossierDtoTypeEnum.Full;
    }
  }

  private setOtherNamesForDossierUpload(): void {
    this.dossierData = this.dossierData.map((dossier) => ({
      ...dossier,
      otherArticleNames: this.otherArticleNames.value
    }));
  }

  private handleSuccessfulDossierUpload(responseStatus: number): void {
    if (responseStatus === 200) {
      //complete Success, all dossiers have been uploaded
      this.notificationService.showSuccess(translationKeys.uploadProductDialogComponent.msg.uploadDossierSuccess);
    } else if (responseStatus === 208) {
      //partial success, some dossiers were uploaded, some are already in an intermediate step in the backend and cannot be updated atm
      this.backendErrorService.handleWarning(undefined, translationKeys.uploadProductDialogComponent.msg.uploadDossierWarning);
    } else {
      this.logger.error('undefined success, please check http response code', responseStatus);
    }
    this.closeEvent.emit(true);
  }
}
